// Promise-based confirmation modal — replaces browser `confirm()`.
//
// CLAUDE.md "Confirmation dialogs" rule: never use `confirm()`; always
// use a styled modal with backdrop + Cancel/Action buttons. Reference
// markup taken from `views/development/step.html.erb` (deletePdfModal).
//
// Usage:
//   import { confirmAction } from "../lib/confirm"
//
//   const ok = await confirmAction({
//     title: "Delete template?",
//     message: "This action cannot be undone.",
//     confirmLabel: "Delete",
//     danger: true
//   })
//   if (!ok) return
//
// Resolves to `true` on confirm, `false` on cancel / backdrop / Esc.
// Always cleans up its own DOM — no leftover modal nodes.

export function confirmAction({
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false
} = {}) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div")
    overlay.className = "fixed inset-0 z-50"
    const confirmBtnClass = danger
      ? "bg-red-600 hover:bg-red-700"
      : "bg-brand hover:bg-brand-dark"

    overlay.innerHTML = `
      <div class="fixed inset-0 bg-black/40" data-confirm-backdrop></div>
      <div class="fixed inset-0 flex items-center justify-center p-4 pointer-events-none">
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-sm pointer-events-auto">
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-900">${escape(title)}</h3>
            <button type="button" data-confirm-cancel
                    class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer"
                    aria-label="${escape(cancelLabel)}">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="px-6 py-4">
            <p class="text-sm text-gray-600">${escape(message)}</p>
          </div>
          <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button type="button" data-confirm-cancel
                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
              ${escape(cancelLabel)}
            </button>
            <button type="button" data-confirm-ok
                    class="px-4 py-2 text-sm font-medium text-white ${confirmBtnClass} rounded-lg cursor-pointer">
              ${escape(confirmLabel)}
            </button>
          </div>
        </div>
      </div>
    `

    const cleanup = (result) => {
      document.removeEventListener("keydown", onKey)
      overlay.remove()
      resolve(result)
    }
    const onKey = (e) => {
      if (e.key === "Escape") cleanup(false)
    }

    overlay.querySelectorAll("[data-confirm-cancel], [data-confirm-backdrop]").forEach((el) => {
      el.addEventListener("click", () => cleanup(false))
    })
    overlay.querySelector("[data-confirm-ok]").addEventListener("click", () => cleanup(true))
    document.addEventListener("keydown", onKey)

    document.body.appendChild(overlay)
    // Focus the confirm button so Enter triggers the action.
    overlay.querySelector("[data-confirm-ok]").focus()
  })
}

function escape(str) {
  return String(str || "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
  }[c]))
}
